/**
 * Plans list themes — one palette per product family.
 *
 * Belongs here: allocation vs rebalance header/timer/sheet-header tokens
 * and risk-profile header art helpers.
 * Does not belong here: body UI colors, or plan-updated banner fill/link/border.
 */

import { AllocationRiskProfile } from '../../../../../expert-review/types/allocation.types'

export type PlansHeaderStrategyPalette = 'allocation' | 'ideal'

export enum PlansThemeFlow {
  ALLOCATION = 'ALLOCATION',
  IDEAL = 'IDEAL',
  REBALANCE = 'REBALANCE',
  SIP_YOUR_PLAN = 'SIP_YOUR_PLAN',
  SIP_IDEAL = 'SIP_IDEAL'
}

export type LumpsumHeaderAppearance = {
  /** Solid top-stop color — status bar, image wash, and top header bar. */
  backgroundColor: string
  /** Full chrome gradient — paint once behind hero + filters/context (not per-section). */
  chromeBackground: string
  riveSrc: string
  /** Temporary static header art until Rive resumes at render. */
  headerSvgSrc: string
  reviewedOnClassName: string
  connectorColor: string
}

/**
 * Full plans chrome + accent for one product family.
 * Edit hexes here; class strings stay literal for Tailwind JIT.
 */
export type PlansTheme = {
  topColor: string
  chromeBackground: string
  connectorColor: string
  /** “Reviewed on” / sheet eyebrow label. */
  headerTextColor: string
  periodLabelClassName: string
  /** Timer icons, info-sheet SVG strokes, unit labels. */
  primary: string
  /** Darker text above a timer CTA. Rebalance digits use this too; Prime digits use primary. */
  primaryDark: string
  primaryTextClassName: string
  primaryDarkTextClassName: string
  primaryDarkShadowClassName: string
}

function buildChromeLinearGradient(parts: {
  topColor: string
  endColor: string
  topStop: string
  endStop: string
}): string {
  return `linear-gradient(180deg, ${parts.topColor} ${parts.topStop}, ${parts.endColor} ${parts.endStop})`
}

/** Allocation + ideal + SIP — prime50 family. */
export const ALLOCATION_PLANS_THEME: PlansTheme = {
  topColor: '#FEF5F3',
  chromeBackground: buildChromeLinearGradient({
    topColor: '#FEF5F3',
    endColor: '#EDCEC4',
    topStop: '41.9%',
    endStop: '162.29%'
  }),
  connectorColor: '#EDCEC4',
  headerTextColor: '#B85230',
  periodLabelClassName: 'text-[13px] font-[400] text-[#B85230]',
  primary: '#B85230',
  primaryDark: '#732E17',
  primaryTextClassName: 'text-[#B85230]',
  primaryDarkTextClassName: 'text-[#732E17]',
  primaryDarkShadowClassName: 'shadow-[2px_2px_0_0_#B85230]'
}

/** Rebalance — elite50 family. */
export const REBALANCE_PLANS_THEME: PlansTheme = {
  topColor: '#FFF7F1',
  chromeBackground: buildChromeLinearGradient({
    topColor: '#FFF7F1',
    endColor: 'rgba(244, 218, 198, 0.80)',
    topStop: '22.57%',
    endStop: '109.92%'
  }),
  connectorColor: '#F4DAC6',
  headerTextColor: '#CC6E30',
  periodLabelClassName: 'text-[13px] font-[400] text-[#CC6E30]',
  primary: '#CC6E30',
  primaryDark: '#A35218',
  primaryTextClassName: 'text-[#CC6E30]',
  primaryDarkTextClassName: 'text-[#A35218]',
  primaryDarkShadowClassName: 'shadow-[2px_2px_0_0_#CC6E30]'
}

/** Resolve theme for a plans flow. Ideal + SIP use allocation. */
export function getPlansFlowTheme(flow: PlansThemeFlow): PlansTheme {
  if (flow === PlansThemeFlow.REBALANCE) {
    return REBALANCE_PLANS_THEME
  }
  return ALLOCATION_PLANS_THEME
}

const RISK_PROFILE_RIVE_BASE = '/rives/risk-profiles'

const DEFAULT_RISK_PROFILE_RIVE = `${RISK_PROFILE_RIVE_BASE}/balanced.riv`

const RISK_PROFILE_RIVE_SRC: Record<AllocationRiskProfile, string> = {
  [AllocationRiskProfile.CONSERVATIVE]: `${RISK_PROFILE_RIVE_BASE}/conservative.riv`,
  [AllocationRiskProfile.BALANCED]: `${RISK_PROFILE_RIVE_BASE}/balanced.riv`,
  [AllocationRiskProfile.AGGRESSIVE]: `${RISK_PROFILE_RIVE_BASE}/aggressive.riv`,
  [AllocationRiskProfile.VERY_AGGRESSIVE]: `${RISK_PROFILE_RIVE_BASE}/very-aggressive.riv`
}

const PLANS_HEADER_V2_BASE = '/images/allocation/plans-header-v2'

/** Allocation + ideal (+ SIP) share the same risk-profile art. */
const ALLOCATION_OR_IDEAL_HEADER_BY_RISK: Record<AllocationRiskProfile, string> = {
  [AllocationRiskProfile.CONSERVATIVE]: `${PLANS_HEADER_V2_BASE}/allocation-conservative.png`,
  [AllocationRiskProfile.BALANCED]: `${PLANS_HEADER_V2_BASE}/allocation-balanced.png`,
  [AllocationRiskProfile.AGGRESSIVE]: `${PLANS_HEADER_V2_BASE}/allocation-aggressive.png`,
  [AllocationRiskProfile.VERY_AGGRESSIVE]: `${PLANS_HEADER_V2_BASE}/allocation-very-aggressive.png`
}

/** Full public paths — each entry can point anywhere; no path convention required. */
export const PlansHeaderSvgSrc = {
  rebalance: `${PLANS_HEADER_V2_BASE}/rebalance.png`,
  allocation: ALLOCATION_OR_IDEAL_HEADER_BY_RISK,
  ideal: ALLOCATION_OR_IDEAL_HEADER_BY_RISK
} as const satisfies {
  rebalance: string
  allocation: Record<AllocationRiskProfile, string>
  ideal: Record<AllocationRiskProfile, string>
}

export function getRiskProfileRiveSrc(riskProfile?: AllocationRiskProfile | null): string {
  if (!riskProfile) {
    return DEFAULT_RISK_PROFILE_RIVE
  }
  return RISK_PROFILE_RIVE_SRC[riskProfile] ?? DEFAULT_RISK_PROFILE_RIVE
}

export function getPlansHeaderSvgSrc({
  riskProfile,
  strategyPalette
}: {
  riskProfile?: AllocationRiskProfile | null
  strategyPalette: PlansHeaderStrategyPalette
}): string {
  const byRiskProfile = PlansHeaderSvgSrc[strategyPalette]
  if (!riskProfile) {
    return byRiskProfile[AllocationRiskProfile.BALANCED]
  }
  return byRiskProfile[riskProfile] ?? byRiskProfile[AllocationRiskProfile.BALANCED]
}

export function resolveHeaderAppearanceWithRiskProfile({
  riskProfile,
  backgroundColor,
  chromeBackground,
  reviewedOnClassName,
  connectorColor,
  strategyPalette
}: {
  riskProfile?: AllocationRiskProfile | null
  backgroundColor: string
  chromeBackground: string
  reviewedOnClassName: string
  connectorColor: string
  strategyPalette: PlansHeaderStrategyPalette
}): LumpsumHeaderAppearance {
  return {
    backgroundColor,
    chromeBackground,
    riveSrc: getRiskProfileRiveSrc(riskProfile),
    headerSvgSrc: getPlansHeaderSvgSrc({ riskProfile, strategyPalette }),
    reviewedOnClassName,
    connectorColor
  }
}

/** Rebalance header art is fixed; riveSrc still follows risk profile for when Rive resumes. */
export function resolveRebalanceHeaderAssetAppearance({
  riskProfile,
  backgroundColor,
  chromeBackground,
  reviewedOnClassName,
  connectorColor
}: {
  riskProfile?: AllocationRiskProfile | null
  backgroundColor: string
  chromeBackground: string
  reviewedOnClassName: string
  connectorColor: string
}): LumpsumHeaderAppearance {
  return {
    backgroundColor,
    chromeBackground,
    riveSrc: getRiskProfileRiveSrc(riskProfile),
    headerSvgSrc: PlansHeaderSvgSrc.rebalance,
    reviewedOnClassName,
    connectorColor
  }
}
